import React, {
  useReducer,
  useState,
  useEffect,
  useCallback,
  ReactElement,
} from "react";
import "../MapStyle.css";
import {
  Title,
  Record,
  useDataProvider,
  useVersion,
  useTranslate,
  useLocale,
  Translate,
  Button,
} from "react-admin";
import resourceNames from "../../../common/data/resourceNames";
import { addDays, parseISO, format, differenceInSeconds } from "date-fns";
import { ReferenceInput } from "../../common/mui-inputs/ReferenceInput";
import { MuiAutocompleteInput } from "../../common/mui-inputs/MuiAutocompleteInput";
import {
  LocationApiFactory,
  LocationHistoryEntryDto,
  VehicleDto,
} from "../../../common/data/service-proxies";
import {
  Typography,
  Slider,
  Tooltip,
  Mark,
  Theme,
  ThemeProvider,
  Paper,
} from "@material-ui/core";
import "./LocationsHistoryView.css";
import { GoogleMap } from "../GoogleMap";
import { GoogleMapMarker } from "../GoogleMapMarker";
import { addSeconds } from "date-fns/esm";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../../common/util/localization";
import { enUS } from "date-fns/locale";
import qs from "qs";
import { RouteComponentProps } from "react-router-dom";
import { useTheme } from "@material-ui/styles";
import { ControlPosition } from "../types";
import { DatePager } from "../../common/mui-inputs/DatePager";
import isNullOrUndefined from "../../../common/util/isNullOrUndefined";
import { List } from "@material-ui/icons";

interface Props extends Partial<RouteComponentProps> {}

interface InfoWindowProps {
  driverFullName?: string | null;
  drivingSpeed?: number | null;
  drivingSpeedUnit?: string | null;
  geolocation?: string | null;
  vehicleMileage?: number | null;
  trailers?: string | null;
  theme: Theme;
  translate: Translate;
}

const DATE_FILTER_SET = "DATE_FILTER_SET";
const VEHICLE_FILTER_SET = "VEHICLE_FILTER_SET";

interface IAction {
  type: "DATE_FILTER_SET" | "VEHICLE_FILTER_SET";
  payload?: string | number;
}

interface IFilter {
  vehicleId?: number;
  date?: string;
}

const dateFilterSet = (date?: Date): IAction => {
  return { type: DATE_FILTER_SET, payload: date?.toISOString() };
};

const vehicleFilterSet = (vehicleId?: number): IAction => {
  return { type: VEHICLE_FILTER_SET, payload: vehicleId };
};

const isNumber = (value?: string | number): value is number => {
  if (!value) {
    return false;
  }

  if (typeof value === "string" || (value as any) instanceof String) {
    return false;
  }

  return true;
};

const InfoWindow = ({
  driverFullName,
  drivingSpeed,
  drivingSpeedUnit,
  geolocation,
  vehicleMileage,
  trailers,
  theme,
  translate,
}: InfoWindowProps) => {
  return (
    <div className="info-window-content">
      <ThemeProvider theme={theme}>
        <Paper
          className="info-window-content-paper"
          style={{ backgroundColor: theme.palette.background.paper }}
        >
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.driverName")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{driverFullName}</Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.trailers")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">
                {trailers ??
                  translate("resources.locations-current.labels.none")}
              </Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.geolocation")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{geolocation ?? "-"}</Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">
                {translate("resources.locations-current.labels.drivingSpeed")}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">
                {drivingSpeed ?? "-"} {drivingSpeedUnit}
              </Typography>
            </div>
          </div>
          <div className="info-window-content-group">
            <div>
              <Typography variant="body2">Vehicle mileage</Typography>
            </div>
            <div>
              <Typography variant="body2">{vehicleMileage ?? "-"}</Typography>
            </div>
          </div>
        </Paper>
      </ThemeProvider>
    </div>
  );
};

const getMapControls = (
  props: InfoWindowProps
): {
  controls: { [name: string]: ReactElement };
  controlPositions: { [name: string]: ControlPosition };
} => {
  return {
    controls: {
      test: <InfoWindow {...props} />,
    },
    controlPositions: { test: "TOP_CENTER" },
  };
};

const reducer = (
  state: IFilter = { date: new Date().toISOString() },
  action: IAction
): IFilter => {
  switch (action.type) {
    case DATE_FILTER_SET:
      if (isNumber(action.payload)) {
        return { ...state };
      }

      return {
        ...state,
        date: action.payload,
      };

    case VEHICLE_FILTER_SET:
      if (!isNumber(action.payload)) {
        return { ...state };
      }

      return { ...state, vehicleId: action.payload };

    default:
      return { ...state };
  }
};

const vehicleSearchFields = ["name"];
const optionFactory = (dataItem: VehicleDto & Record) =>
  dataItem.id && dataItem.name
    ? { id: dataItem.id, name: dataItem.name }
    : undefined;

const getError = (error: any) =>
  error ? (!!error.message ? error.message : error.toString()) : undefined;

const getLocationTime = (id: number, locale: Locale) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setSeconds(id);
  return format(date, "p", { locale });
};

interface ValueLabelProps {
  children: React.ReactElement;
  open: boolean;
  value: number;
}

const ValueLabelComponent = (props: ValueLabelProps) => {
  let { children, open, value } = props;
  value = value ?? "00:00";
  return (
    <Tooltip open={open} enterTouchDelay={0} placement="top" title={value}>
      {children}
    </Tooltip>
  );
};

export const LocationsHistoryView = (props: Props) => {
  const { history } = props;

  let initialFilter = {
    date: new Date().toISOString(),
    vehicleId: undefined,
  };
  if (history?.location.search.length) {
    const parsedQuery = qs.parse(history.location.search.substring(1));
    initialFilter = { ...initialFilter, ...parsedQuery };
  }

  const theme: Theme = useTheme();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const version = useVersion();
  const [loading, setLoading] = useState<boolean>();
  const [data, setData] = useState<Array<LocationHistoryEntryDto>>();
  const [error, setError_] = useState<string | undefined>(
    translate("resources.locations-history.labels.pleaseSelectVehicle")
  );
  const [isErrorSet, setIsErrorSet] = useState<boolean>(false);
  const [second, setSecond] = useState<number>();
  const [marks, setMarks] = useState<Mark[]>();
  const [highlightedRoute, setHighlightedRoute] = useState<
    Array<{ lat: number; lng: number }>
  >();
  const [visitedRoute, setVisitedRoute] = useState<
    Array<{ lat: number; lng: number }>
  >();
  const [currentLocation, setCurrentLocation] = useState<
    LocationHistoryEntryDto
  >();
  const [filter, dispatch] = useReducer(reducer, initialFilter);
  const [dayStart, setDayStart] = useState<Date>();
  const [odometerUnit, setOdometerUnit] = useState<string>();
  const axiosInstance = useAxiosInstance();
  const locale = useLocale() as ProLogsLocale;
  const dateFnsLocale = !!dateFnsLocaleMap[locale]
    ? dateFnsLocaleMap[locale]
    : enUS;

  const setError = useCallback((error: string | undefined) => {
    setIsErrorSet(true);
    setError_(error);
  }, []);

  useEffect(() => {
    if (!isErrorSet) {
      setError_(
        translate("resources.locations-history.labels.pleaseSelectVehicle")
      );
    }
  }, [isErrorSet, translate, locale]);

  useEffect(() => {
    if (!axiosInstance) {
      return;
    }

    if (!filter.vehicleId) {
      return;
    }

    setLoading(true);
    LocationApiFactory(undefined, axiosInstance.defaults.baseURL, axiosInstance)
      .apiAppLocationVehicleLocationHistoryGet(filter.vehicleId, filter.date)
      .then((result) => {
        if (!result.data.locations || !result.data.locations.length) {
          setError(
            translate(
              "resources.locations-history.labels.noDataForSelectedFilter"
            )
          );
          return;
        }

        setError(undefined);
        setData(result.data.locations);
        setCurrentLocation(result.data.locations[0]);
        setSecond(
          differenceInSeconds(
            parseISO(result.data.locations[0].timeStamp!),
            parseISO(result.data.startOfDay!)
          )
        );
        const marksToSet = result.data.locations.map((x) => {
          const seconds = differenceInSeconds(
            parseISO(x.timeStamp!),
            parseISO(result.data.startOfDay!)
          );
          if (!seconds) {
            console.warn(
              `Unable to get seconds for timestamp '${x.timeStamp}'`
            );
          }

          return { value: seconds, label: "" };
        });
        setMarks(marksToSet);
        setDayStart(parseISO(result.data.startOfDay!));
        if (result.data.drivingSpeedUnit) {
          setOdometerUnit(result.data.drivingSpeedUnit);
        }
      })
      .catch((e) => {
        setError(getError(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataProvider, filter, version, axiosInstance, translate, setError]);

  useEffect(() => {
    if (!data) {
      return;
    }

    if (!currentLocation) {
      return;
    }

    if (!dayStart) {
      return;
    }

    const locationIndex = data.indexOf(currentLocation);
    const visited = [...data.slice(0, locationIndex), currentLocation];
    const highlighted = data.slice(locationIndex);

    setHighlightedRoute(highlighted.map((x) => ({ lat: x.lat!, lng: x.lng! })));

    setVisitedRoute(visited.map((x) => ({ lat: x.lat!, lng: x.lng! })));
  }, [data, currentLocation, dayStart]);

  return (
    <div className="content-container locations-history">
      <Title title="resources.locations-history.name" />
      <div className="locations-history-toolbar">
        <ReferenceInput
          resource={resourceNames.LOCATIONS_HISTORY_VEHICLES}
          value={filter.vehicleId}
        >
          <MuiAutocompleteInput<VehicleDto & Record>
            className="vehicle-selector"
            label={translate("resources.locations-history.labels.vehicle")}
            searchFields={vehicleSearchFields}
            optionFactory={optionFactory}
            onSelect={(value) => {
              dispatch(vehicleFilterSet(value?.id));
              const query = qs.stringify(
                { ...filter, vehicleId: value?.id },
                { encode: false }
              );
              history?.push({
                pathname: history.location.pathname,
                search: "?" + query,
              });
            }}
          />
        </ReferenceInput>
        <div className="right-side">
          <DatePager
            value={filter?.date ? parseISO(filter.date) : new Date()}
            onSetCurrentDate={(date) => {
              if (!!date) {
                dispatch(dateFilterSet(date));
                const query = qs.stringify(
                  { ...filter, date },
                  { encode: false }
                );
                history?.push({
                  pathname: history.location.pathname,
                  search: "?" + query,
                });
              }
            }}
            previousEnabled
            nextEnabled
            previousOnClick={() => {
              const newDate = addDays(parseISO(filter.date!), -1);
              dispatch(dateFilterSet(newDate));
              const query = qs.stringify(
                { ...filter, date: newDate },
                { encode: false }
              );
              history?.push({
                pathname: history.location.pathname,
                search: "?" + query,
              });
            }}
            nextOnClick={() => {
              const newDate = addDays(parseISO(filter.date!), 1);
              dispatch(dateFilterSet(newDate));
              const query = qs.stringify(
                { ...filter, date: newDate },
                { encode: false }
              );
              history?.push({
                pathname: history.location.pathname,
                search: "?" + query,
              });
            }}
          />
          <Button
            variant="contained"
            label="Back to list"
            onClick={() => history?.goBack()}
          >
            <List />
          </Button>
        </div>
      </div>

      <GoogleMap
        loading={loading}
        error={getError(error)}
        criticalError={!!error}
        alwaysPan
        highlightedRoute={highlightedRoute}
        visitedRoute={visitedRoute}
        panToHighlightedRoute
        {...getMapControls({
          driverFullName: currentLocation?.driverDisplayName,
          drivingSpeed: currentLocation?.drivingSpeed,
          drivingSpeedUnit: odometerUnit,
          trailers: currentLocation?.attachedTrailers,
          geolocation: currentLocation?.geolocation,
          vehicleMileage: currentLocation?.vehicleOdometer,
          theme,
          translate,
        })}
      >
        {!!currentLocation &&
        !isNullOrUndefined(currentLocation.lat) &&
        !isNullOrUndefined(currentLocation.lng) &&
        !isNullOrUndefined(currentLocation.bearing) ? (
          <GoogleMapMarker
            lat={currentLocation.lat}
            lng={currentLocation.lng}
            bearing={currentLocation.bearing}
          />
        ) : null}
      </GoogleMap>
      <div className="history-slider">
        {!loading && data && !error && dayStart && (
          <Slider
            min={0}
            max={86399}
            step={60}
            value={second ?? 0}
            onChange={(_, v) => {
              const sec = v as number;
              const dataItem = data.find(
                (x) => parseISO(x.timeStamp!) >= addSeconds(dayStart, sec)
              );
              if (dataItem) {
                setCurrentLocation(dataItem);
              }

              setSecond(sec);
            }}
            ValueLabelComponent={ValueLabelComponent}
            valueLabelDisplay="on"
            valueLabelFormat={(id) => getLocationTime(id, dateFnsLocale)}
            marks={marks}
            track={false}
          />
        )}
      </div>
    </div>
  );
};
